/**
 * MapErrorBoundary Component
 *
 * Error boundary specifically for map components to handle:
 * - GeoJSON loading failures
 * - Google Maps API errors
 * - Data fetching errors
 * - Rendering crashes
 *
 * @version 1.0.0
 * @since 2026-05-21
 */

import { Component, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, XCircle } from 'lucide-react'

interface MapErrorBoundaryProps {
  children: ReactNode
  /** Optional fallback UI (if not provided, uses default) */
  fallback?: ReactNode
  /** Optional callback when error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface MapErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

/**
 * Error Boundary for Map Components
 *
 * Usage:
 * ```tsx
 * <MapErrorBoundary>
 *   <PersonnelMapPolygon />
 * </MapErrorBoundary>
 * ```
 */
export class MapErrorBoundary extends Component<MapErrorBoundaryProps, MapErrorBoundaryState> {
  constructor(props: MapErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<MapErrorBoundaryState> {
    // Update state so next render shows fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console
    console.error('MapErrorBoundary caught an error:', error, errorInfo)

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    })

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Error tracking integration
    // NOTE: Sentry integration planned for Phase 3.2 (Post-MVP)
    // When implemented, uncomment and configure:
    // if (import.meta.env.PROD && window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     contexts: {
    //       react: {
    //         componentStack: errorInfo?.componentStack,
    //       },
    //     },
    //   })
    // }
    // See: docs/deployment/MVP_RELEASE_CHECKLIST.md - Step 8: Monitoring Setup
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  getErrorType(error: Error | null): 'geojson' | 'api' | 'maps' | 'unknown' {
    if (!error) return 'unknown'

    const message = error.message.toLowerCase()

    if (message.includes('geojson') || message.includes('fetch')) {
      return 'geojson'
    }
    if (message.includes('google') || message.includes('maps')) {
      return 'maps'
    }
    if (message.includes('api') || message.includes('network')) {
      return 'api'
    }

    return 'unknown'
  }

  getErrorMessage(errorType: string): { title: string; description: string } {
    switch (errorType) {
      case 'geojson':
        return {
          title: 'ไม่สามารถโหลดขอบเขตอำเภอ',
          description: 'ไฟล์ GeoJSON โหลดไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
        }
      case 'api':
        return {
          title: 'ไม่สามารถโหลดข้อมูล',
          description: 'เชื่อมต่อ API ไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
        }
      case 'maps':
        return {
          title: 'Google Maps โหลดไม่สำเร็จ',
          description: 'ไม่สามารถเริ่มต้น Google Maps API กรุณาลองใหม่อีกครั้ง',
        }
      default:
        return {
          title: 'เกิดข้อผิดพลาด',
          description: 'แผนที่แสดงผลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
        }
    }
  }

  render() {
    if (this.state.hasError) {
      // If custom fallback provided, use it
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Otherwise, use default error UI
      const errorType = this.getErrorType(this.state.error)
      const { title, description } = this.getErrorMessage(errorType)

      return (
        <div className="bg-white rounded-xl border-2 border-korat-navy-200 shadow-lg p-12">
          <div className="max-w-md mx-auto text-center">
            {/* Error Icon */}
            <div className="mb-6 flex justify-center">
              {errorType === 'geojson' || errorType === 'maps' ? (
                <XCircle className="w-16 h-16 text-orange-500" />
              ) : (
                <AlertTriangle className="w-16 h-16 text-orange-500" />
              )}
            </div>

            {/* Error Title */}
            <h3 className="text-xl font-bold text-korat-navy-900 mb-3">{title}</h3>

            {/* Error Description */}
            <p className="text-korat-navy-600 mb-6">{description}</p>

            {/* Retry Button */}
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-6 py-3 bg-korat-navy-600 hover:bg-korat-navy-700 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              <RefreshCw className="w-5 h-5" />
              <span>ลองใหม่อีกครั้ง</span>
            </button>

            {/* Technical Details (development only) */}
            {typeof process !== 'undefined' && process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-sm font-semibold text-korat-navy-700 hover:text-korat-navy-900 mb-2">
                  รายละเอียดข้อผิดพลาด (Development)
                </summary>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-xs font-mono overflow-auto max-h-64">
                  <div className="text-red-700 font-bold mb-2">
                    {this.state.error.name}: {this.state.error.message}
                  </div>
                  <pre className="text-red-600 whitespace-pre-wrap">
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo && (
                    <>
                      <div className="text-red-700 font-bold mt-4 mb-2">Component Stack:</div>
                      <pre className="text-red-600 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Lightweight wrapper for functional components (alternative approach)
 *
 * Usage:
 * ```tsx
 * export default withMapErrorBoundary(PersonnelMapPolygon)
 * ```
 */
export function withMapErrorBoundary<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return function MapErrorBoundaryWrapper(props: P) {
    return (
      <MapErrorBoundary>
        <Component {...props} />
      </MapErrorBoundary>
    )
  }
}
